import { useState } from 'react';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import type { WorkQueueCase } from '../../../../services/workQueue';
import { ConfirmModalShell } from '../approval-review/ConfirmModalShell';
import { ConfirmModalHeader } from '../approval-review/ConfirmModalHeader';
import { ConfirmModalBody } from '../approval-review/ConfirmModalBody';
import { ConfirmModalFooter } from '../approval-review/ConfirmModalFooter';
import { PatientSummaryCard } from '../approval-review/PatientSummaryCard';
import { ReasonField } from '../approval-review/ReasonField';
import {
    GO_AHEAD_REASON_MAX_LENGTH,
    GO_AHEAD_REASON_MIN_LENGTH,
    isValidGoAheadReason,
    type GoAheadChoice
} from './goAheadChoices';

interface GoAheadReasonModalProps {
    workCase: WorkQueueCase;
    choice: GoAheadChoice;
    submitting: boolean;
    onCancel: () => void;
    onConfirm: (reason: string) => void;
}

export function GoAheadReasonModal({
    workCase: c,
    choice,
    submitting,
    onCancel,
    onConfirm
}: GoAheadReasonModalProps) {
    const [reason, setReason] = useState('');

    return (
        <ConfirmModalShell
            widthClassName="w-[460px]"
            dismissible={!submitting}
            onDismiss={onCancel}
        >
            <ConfirmModalHeader
                icon={ChatBubbleBottomCenterTextIcon}
                iconClassName="w-5 h-5 text-gray-500"
                title={choice.label}
                onClose={onCancel}
                disabled={submitting}
            />
            <ConfirmModalBody className="space-y-3">
                <PatientSummaryCard name={c.name} practice={c.practice} />
                <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    {choice.consequence}
                </p>
                <ReasonField
                    value={reason}
                    onChange={setReason}
                    disabled={submitting}
                    label="Orthodontist's reason"
                    placeholder="Recorded on the timeline as the orthodontist's decision."
                    minLength={GO_AHEAD_REASON_MIN_LENGTH}
                    maxLength={GO_AHEAD_REASON_MAX_LENGTH}
                    tone="amber"
                />
            </ConfirmModalBody>
            <ConfirmModalFooter
                onCancel={onCancel}
                onConfirm={() => onConfirm(reason)}
                cancelDisabled={submitting}
                confirmDisabled={submitting || !isValidGoAheadReason(reason)}
                confirmLabel={submitting ? 'Submitting…' : choice.label}
                confirmColorClassName="bg-gray-800 text-white hover:bg-gray-900"
            />
        </ConfirmModalShell>
    );
}
